/* import __COLOCATED_TEMPLATE__ from './custom-object-composer.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { BlocksDocument } from '@intercom/composer-ember';
import ActionComposerConfig, {
  CustomActionsAttributeInfoResolver,
} from 'embercom/lib/actions/action-composer-config';
import { action } from '@ember/object';
import { tracked } from 'tracked-built-ins';
import { inject as service } from '@ember/service';
import { type Paragraph } from 'embercom/models/common/blocks/paragraph';
import { htmlUnescape } from 'embercom/lib/html-unescape';

interface Args {
  value: any;
  includeAppAttributes: boolean;
  isDisabled?: boolean;
  localAttributes: any;
  attributeInfoResolver?: CustomActionsAttributeInfoResolver | null;
  isSingleLineComposer?: boolean;
  class?: string;
  errors?: any;
  placeholder?: string;
  unprotectedAttributes?: string[];
  onChange: (value: any) => void;
}

interface Signature {
  Args: Args;
}

export default class CustomObjectComposer extends Component<Signature> {
  @service declare appService: AppService;
  @service declare attributeService: any;

  @tracked blocksDoc: BlocksDocument;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.blocksDoc = new BlocksDocument(this.args.value);
  }

  get composerConfig() {
    let resolver =
      this.args.attributeInfoResolver ||
      new CustomActionsAttributeInfoResolver({
        attributes: this.appService.app.allowedAttributes,
        customObjectAttributes: this.attributeService.customObjectsAttributeDescriptors,
        conversationAttributes: this.attributeService.conversationAttributesForCustomAction,
        includeFileAttributes: this.appService.app.canUseFeature(
          'rd-services-file-attributes-in-data-connector',
        ),
        ticketTypeAttributes: this.attributeService.ticketTypeAttributes,
        includeCustomObjectAttributes: true,
        includeCustomActionAttributes: true,
        includeAppAttributes: this.args.includeAppAttributes,
        localAttributes: this.args.localAttributes,
        unprotectedAttributes: this.args.unprotectedAttributes,
      });

    return new ActionComposerConfig(
      this.args.isSingleLineComposer,
      this.args.isDisabled,
      resolver,
      this.args.placeholder,
    );
  }

  @action
  onChange(document: BlocksDocument) {
    this.blocksDoc = document;
    this.blocksDoc.blocks = this.blocksDoc.blocks.map((block) => {
      if (block.type === 'paragraph') {
        return {
          ...block,
          text: htmlUnescape(block.text),
        };
      }
      return block;
    });
    this.args.onChange(this.blocksDoc);
  }

  get hasContent() {
    let { blocks } = this.blocksDoc;
    if (blocks.length !== 1) {
      return blocks.length > 0;
    }
    let firstBlock = blocks[0];
    return firstBlock.type !== 'paragraph' || (firstBlock as Paragraph).text !== '';
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'WorkflowConnector::Builder::Body::Sections::Request::CustomObjectComposer': typeof CustomObjectComposer;
    'workflow-connector/builder/body/sections/request/custom-object-composer': typeof CustomObjectComposer;
  }
}
